import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "services/api";
import { useAuth } from "context/AuthContext";
import { NipperReading } from "utils/nipper/nipperSession";

export type UpgradeStatus = "eligible" | "alreadyUpgraded" | "notEligible";

export interface EcoSaltUnit {
  nipperSerial: string;
  originalCellSize: number;
  originalRecordedAt: string;
  upgradedAt?: string | null;
  upgradedCellSize?: number | null;
}

export interface EcoSaltUnitState {
  unit: EcoSaltUnit;
  evaluation: { status: UpgradeStatus; target?: number; reason?: string };
  reconciled: boolean;
}

export interface EcoSaltCompleteResult {
  nipperSerial: string;
  originalCellSize: number;
  upgradedCellSize: number;
  upgradedAt: string;
}

const BASE = "dpp/ecosalt-upgrade";
const unitPath = (serial: string) => `${BASE}/units/${encodeURIComponent(serial)}`;

/** API body for a Nipper reading (POST .../reads and .../upgrade/complete). */
export function toSnapshotBody(reading: NipperReading) {
  const { variant, runtime } = reading;
  return {
    cellSize: variant.cellSize,
    region: variant.region,
    brand: variant.brand,
    lowSalt: variant.lowSalt,
    firmwareVersion: reading.firmwareVersion,
    runtimeFormat: runtime.format,
    cellRuntime: runtime.cellRuntime,
    lowSaltRuntime: runtime.lowSaltRuntime,
    cell2Count: runtime.cell2Count,
    cell2Runtime: runtime.cell2Runtime,
    cell2LowSaltRuntime: runtime.cell2LowSaltRuntime,
    raw: reading.raw,
  };
}

/** Whether the signed-in user has the EcoSalt Upgrade feature flag. */
export const useEcoSaltEligibility = () => {
  const { user, isAuthenticated } = useAuth();
  const canCheck = isAuthenticated && user?.id != null;
  const query = useQuery({
    queryKey: ["ecoSaltEligibility", user?.id],
    enabled: canCheck,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async () => (await api.get<{ enabled: boolean }>(`${BASE}/eligibility`)).data,
  });
  return {
    enabled: canCheck && query.data?.enabled === true,
    isLoading: canCheck && query.isLoading,
  };
};

export const useRecordNipperRead = () =>
  useMutation({
    mutationFn: async ({ serial, reading }: { serial: string; reading: NipperReading }) =>
      (await api.post<EcoSaltUnitState>(`${unitPath(serial)}/reads`, toSnapshotBody(reading))).data,
  });

export const useAuthorizeUpgrade = () =>
  useMutation({
    mutationFn: async ({ serial, currentCellSize }: { serial: string; currentCellSize: number }) =>
      (
        await api.post<{ nipperSerial: string; originalCellSize: number; targetCellSize: number }>(
          `${unitPath(serial)}/upgrade/authorize`,
          { currentCellSize },
        )
      ).data,
  });

export const useCompleteUpgrade = () =>
  useMutation({
    mutationFn: async ({ serial, reading }: { serial: string; reading: NipperReading }) =>
      (await api.post<EcoSaltCompleteResult>(`${unitPath(serial)}/upgrade/complete`, toSnapshotBody(reading))).data,
  });

/* ----- admin ----- */

export interface EcoSaltUpgradeListItem {
  nipperSerial: string;
  fromCellSize: number;
  toCellSize: number;
  upgradedAt: string;
  dealerEmail: string | null;
  dealerName: string | null;
}

/** Completed EcoSalt upgrades (admin). */
export const useEcoSaltUpgradeList = (enabled: boolean) =>
  useQuery({
    queryKey: ["ecoSaltUpgrades"],
    enabled,
    queryFn: async () =>
      (await api.get<{ upgrades: EcoSaltUpgradeListItem[] }>("dpp/admin/ecosalt-upgrades")).data,
  });

/** Deletes one EcoSalt record (admin) and refreshes the dashboard list. */
export const useDeleteEcoSaltUpgrade = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (serial: string) =>
      (await api.delete(`dpp/admin/ecosalt-upgrades/${encodeURIComponent(serial)}`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ecoSaltUpgrades"] }),
  });
};

/** Downloads the EcoSalt upgrade report CSV (admin). */
export async function downloadEcoSaltReport(): Promise<void> {
  const response = await api.get<Blob>("dpp/admin/ecosalt-upgrades/export", { responseType: "blob" });
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data as any], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `ecosalt-upgrades-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
